(() => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const responseCache = new Map();

  const debounce = (callback, delay = 350) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), delay);
    };
  };

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  };

  class PupPlanetPriceRange extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.rangeMin = this.querySelector('[data-pp-range-min]');
      this.rangeMax = this.querySelector('[data-pp-range-max]');
      this.numberMin = this.querySelector('[data-pp-price-min]');
      this.numberMax = this.querySelector('[data-pp-price-max]');
      this.track = this.querySelector('.pp-price-range__track');
      if (!this.rangeMin || !this.rangeMax || !this.numberMin || !this.numberMax) return;

      this.rangeMin.addEventListener('input', () => this.syncFromRange('min'));
      this.rangeMax.addEventListener('input', () => this.syncFromRange('max'));
      this.rangeMin.addEventListener('change', () => this.emitNumberChange(this.numberMin));
      this.rangeMax.addEventListener('change', () => this.emitNumberChange(this.numberMax));
      this.numberMin.addEventListener('change', () => this.syncFromNumber('min'));
      this.numberMax.addEventListener('change', () => this.syncFromNumber('max'));
      this.updateTrack();
    }

    maxValue() {
      return Math.max(Number(this.dataset.max) || 0, 1);
    }

    syncFromRange(changed) {
      let min = Number(this.rangeMin.value) || 0;
      let max = Number(this.rangeMax.value) || this.maxValue();
      if (min > max) {
        if (changed === 'min') min = max;
        else max = min;
      }
      this.rangeMin.value = min;
      this.rangeMax.value = max;
      this.numberMin.value = min === 0 ? '' : min;
      this.numberMax.value = max === this.maxValue() ? '' : max;
      this.updateTrack();
    }

    syncFromNumber(changed) {
      let min = this.numberMin.value === '' ? 0 : Number(this.numberMin.value);
      let max = this.numberMax.value === '' ? this.maxValue() : Number(this.numberMax.value);
      min = Math.max(0, Math.min(min || 0, this.maxValue()));
      max = Math.max(0, Math.min(max || this.maxValue(), this.maxValue()));
      if (min > max) {
        if (changed === 'min') min = max;
        else max = min;
      }
      this.rangeMin.value = min;
      this.rangeMax.value = max;
      this.numberMin.value = min === 0 ? '' : min;
      this.numberMax.value = max === this.maxValue() ? '' : max;
      this.updateTrack();
    }

    updateTrack() {
      if (!this.track) return;
      const max = this.maxValue();
      const minPercent = (Number(this.rangeMin.value) / max) * 100;
      const maxPercent = (Number(this.rangeMax.value) / max) * 100;
      this.track.style.background = `linear-gradient(to right, var(--pp-filter-line) 0 ${minPercent}%, var(--pp-filter-accent-two) ${minPercent}% ${maxPercent}%, var(--pp-filter-line) ${maxPercent}% 100%)`;
    }

    emitNumberChange(input) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  class PupPlanetCollectionFilters extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.form = this.querySelector('form');
      this.sectionId = this.dataset.sectionId;
      this.abortController = null;
      this.lastFocusedElement = null;
      this.submitDebounced = debounce(() => this.submitFilters(false), 380);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('keydown', this.onKeyDown.bind(this));
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.form.addEventListener('change', this.onChange.bind(this));
      }

      this.querySelector('dialog')?.addEventListener('cancel', (event) => { event.preventDefault(); this.closeFilters(); });
      this.setupValueLimits();
      PupPlanetCollectionFilters.bindHistory();
      PupPlanetCollectionFilters.measureHeader();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.classList.contains('is-open')) document.body.classList.remove('pp-filters-open');
    }

    static bindHistory() {
      if (this.historyBound) return;
      this.historyBound = true;
      window.addEventListener('popstate', () => {
        const filterElement = document.querySelector('pp-collection-filters');
        if (filterElement) filterElement.render(window.location.pathname, window.location.search.slice(1), false, false);
      });

      const header = document.querySelector('.shopify-section-group-header-group');
      if (header && 'ResizeObserver' in window) {
        this.headerObserver = new ResizeObserver(() => this.measureHeader());
        this.headerObserver.observe(header);
      }
      window.addEventListener('resize', debounce(() => this.measureHeader(), 100), { passive: true });
    }

    static measureHeader() {
      const header = document.querySelector('.shopify-section-group-header-group');
      if (!header) return;
      const height = Math.ceil(header.getBoundingClientRect().height + 8);
      document.documentElement.style.setProperty('--pp-header-offset', `${height}px`);
    }

    onClick(event) {
      const openButton = event.target.closest('[data-pp-filter-open]');
      if (openButton) {
        event.preventDefault();
        this.openFilters(openButton);
        return;
      }

      const closeButton = event.target.closest('[data-pp-filter-close]');
      if (closeButton) {
        event.preventDefault();
        this.closeFilters();
        return;
      }

      const showMoreButton = event.target.closest('[data-pp-show-more]');
      if (showMoreButton) {
        event.preventDefault();
        this.toggleShowMore(showMoreButton);
        return;
      }

      const filterLink = event.target.closest('[data-pp-filter-link]');
      if (filterLink && filterLink.getAttribute('aria-disabled') !== 'true') {
        event.preventDefault();
        const target = new URL(filterLink.href, window.location.origin);
        const closeAfter = Boolean(filterLink.closest('.pp-filter-panel'));
        this.render(target.pathname, target.search.slice(1), true, closeAfter);
      }
    }

    onInput(event) {
      const searchInput = event.target.closest('[data-pp-value-search]');
      if (!searchInput) return;
      this.filterValues(searchInput);
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-pp-value-search]')) return;
      if (target.matches('[data-pp-sort]')) {
        return;
      }

      if (target.matches('.pp-filter-choice__input')) {
        const choice = target.closest('.pp-filter-choice');
        if (choice) choice.classList.toggle('is-selected', target.checked);
      }

      
    }

    onSubmit(event) {
      event.preventDefault();
      this.submitFilters(true);
    }

    onKeyDown(event) {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        this.closeFilters();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = this.querySelector('.pp-filter-panel');
      const focusable = Array.from(panel?.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), summary') || [])
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openFilters(trigger) {
      this.lastFocusedElement = trigger || document.activeElement;
      this.querySelector('dialog')?.showModal();
      this.classList.add('is-open');
      document.body.classList.add('pp-filters-open');
      const openButton = this.querySelector('[data-pp-filter-open]');
      if (openButton) openButton.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => this.querySelector('[data-pp-filter-close]:not(.pp-filter-backdrop)')?.focus());
    }

    closeFilters(restoreFocus = true) {
      this.querySelector('dialog')?.close();
      this.classList.remove('is-open');
      document.body.classList.remove('pp-filters-open');
      const openButton = this.querySelector('[data-pp-filter-open]');
      if (openButton) openButton.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.lastFocusedElement && document.contains(this.lastFocusedElement)) this.lastFocusedElement.focus();
    }

    setupValueLimits() {
      this.querySelectorAll('[data-pp-value-list]').forEach((list) => {
        list.dataset.expanded = 'false';
        this.applyValueLimit(list);
      });
    }

    applyValueLimit(list) {
      const limit = Math.max(Number(list.dataset.limit) || 10, 1);
      const expanded = list.dataset.expanded === 'true';
      const hasSearch = Boolean(list.closest('.pp-filter-group')?.querySelector('[data-pp-value-search]')?.value.trim());
      const items = Array.from(list.querySelectorAll('[data-pp-filter-value]'));
      let visibleIndex = 0;
      items.forEach((item) => {
        const searchMatch = item.dataset.searchMatch !== 'false';
        const withinLimit = expanded || visibleIndex < limit;
        item.hidden = !searchMatch || (!hasSearch && !withinLimit);
        if (searchMatch) visibleIndex += 1;
      });

      const showMore = list.querySelector('[data-pp-show-more]');
      if (showMore) showMore.hidden = hasSearch || items.length <= limit;
      const empty = list.querySelector('[data-pp-values-empty]');
      if (empty) empty.hidden = items.some((item) => item.dataset.searchMatch !== 'false');
    }

    toggleShowMore(button) {
      const list = button.closest('[data-pp-value-list]');
      if (!list) return;
      const expanded = list.dataset.expanded !== 'true';
      list.dataset.expanded = String(expanded);
      button.setAttribute('aria-expanded', String(expanded));
      button.querySelector('[data-pp-show-more-label]')?.toggleAttribute('hidden', expanded);
      button.querySelector('[data-pp-show-less-label]')?.toggleAttribute('hidden', !expanded);
      this.applyValueLimit(list);
    }

    filterValues(input) {
      const list = input.closest('.pp-filter-group')?.querySelector('[data-pp-value-list]');
      if (!list) return;
      const query = input.value.trim().toLocaleLowerCase();
      list.querySelectorAll('[data-pp-filter-value]').forEach((item) => {
        item.dataset.searchMatch = String(!query || item.dataset.valueLabel.includes(query));
      });
      this.applyValueLimit(list);
    }

    submitFilters(closeAfter) {
      if (!this.form) return;
      const params = new URLSearchParams(new FormData(this.form));
      Array.from(params.entries()).forEach(([key, value]) => {
        if (value === '') params.delete(key);
      });
      this.render(window.location.pathname, params.toString(), true, closeAfter);
    }

    async render(pathname, searchParams, updateHistory = true, closeAfter = false) {
      if (!this.sectionId) {
        window.location.assign(`${pathname}${searchParams ? `?${searchParams}` : ''}`);
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const sectionUrl = `${pathname}?section_id=${encodeURIComponent(this.sectionId)}${searchParams ? `&${searchParams}` : ''}`;
      const destinationUrl = `${pathname}${searchParams ? `?${searchParams}` : ''}`;
      const openKeys = Array.from(this.querySelectorAll('[data-pp-filter-key][open]')).map((detail) => detail.dataset.ppFilterKey);
      const focusedInput = document.activeElement?.matches('input, select')
        ? { name: document.activeElement.name, value: document.activeElement.value }
        : null;

      this.setLoading(true);
      if (closeAfter) this.closeFilters(false);

      try {
        let html = responseCache.get(sectionUrl);
        if (!html) {
          const response = await fetch(sectionUrl, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.abortController.signal,
          });
          if (!response.ok) throw new Error(`Filter request failed (${response.status})`);
          html = await response.text();
          responseCache.set(sectionUrl, html);
          if (responseCache.size > 20) responseCache.delete(responseCache.keys().next().value);
        }

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextFilters = parsed.querySelector(`#PPCollectionFilters-${escapeSelector(this.sectionId)}`);
        const nextGrid = parsed.querySelector('#ProductGridContainer');
        const currentGrid = document.querySelector('#ProductGridContainer');
        if (!nextFilters || !nextGrid || !currentGrid) throw new Error('The refreshed collection section was incomplete');

        openKeys.forEach((key) => {
          nextFilters.querySelector(`[data-pp-filter-key="${escapeSelector(key)}"]`)?.setAttribute('open', '');
        });

        currentGrid.replaceWith(nextGrid);
        this.replaceWith(nextFilters);

        if (window.PupPlanetDogHelper && typeof window.PupPlanetDogHelper.refresh === 'function') {
          window.PupPlanetDogHelper.refresh();
        }

        if (updateHistory) window.history.pushState({ ppFilters: searchParams }, '', destinationUrl);

        window.requestAnimationFrame(() => {
          if (closeAfter) nextFilters.querySelector('[data-pp-filter-open]')?.focus({ preventScroll: true });
          if (!closeAfter && focusedInput?.name && desktopQuery.matches) {
            const candidates = Array.from(nextFilters.querySelectorAll(`[name="${escapeSelector(focusedInput.name)}"]`));
            const match = candidates.find((input) => input.value === focusedInput.value) || candidates[0];
            match?.focus({ preventScroll: true });
          }
          const countText = nextFilters.querySelector('#ProductCount')?.textContent?.replace(/\s+/g, ' ').trim();
          const announcer = nextFilters.querySelector('[data-pp-filter-announcer]');
          if (announcer && countText) announcer.textContent = `Filters updated. ${countText}.`;
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(destinationUrl);
      }
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      this.setAttribute('aria-busy', String(loading));
      document.querySelector('#ProductGridContainer')?.classList.toggle('pp-filter-loading', loading);
    }
  }

  PupPlanetCollectionFilters.historyBound = false;

  if (!customElements.get('pp-price-range')) customElements.define('pp-price-range', PupPlanetPriceRange);
  if (!customElements.get('pp-collection-filters')) customElements.define('pp-collection-filters', PupPlanetCollectionFilters);
})();
